import { useMemo } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { LenderTag, DisbursementTranche } from "../../types/deals";
import { Building2, TrendingUp, TrendingDown, Minus, Calendar, AlertTriangle } from "lucide-react";
import { format, parseISO, isBefore, addDays } from "date-fns";
import { getStatusConfig } from "../../utils/statusConfig";

interface LimitCardProps {
  lender: LenderTag;
  disbursements: DisbursementTranche[];
}

export function LimitCard({ lender, disbursements }: LimitCardProps) {
  const { sanctioned, utilized, available, utilizationPercent, latestStatus, sanctionDate, sanctionValidTill, isExpiringSoon, isExpired } = useMemo(() => {
    // Get sanctioned amount and dates from status history
    const sanctionEntry = [...lender.statusHistory]
      .reverse()
      .find((e) => typeof e.sanctionAmount === "number");
    const sanctionedAmount = sanctionEntry?.sanctionAmount ?? 0;
    const sDate = sanctionEntry?.sanctionDate;
    const sValidTill = sanctionEntry?.sanctionValidTill;

    // Calculate utilized from disbursements linked to this lender
    const utilizedAmount = disbursements
      .filter((d) => d.lenderId === lender.institutionId)
      .reduce((sum, d) => sum + d.disbursedAmount, 0);

    const availableAmount = Math.max(0, sanctionedAmount - utilizedAmount);
    const utilization = sanctionedAmount > 0 ? (utilizedAmount / sanctionedAmount) * 100 : 0;

    const status = lender.statusHistory[lender.statusHistory.length - 1]?.status;

    // Check expiry status
    let expiringSoon = false;
    let expired = false;
    if (sValidTill) {
      const validTillDate = parseISO(sValidTill);
      const today = new Date();
      const warningDate = addDays(today, 30);
      expired = isBefore(validTillDate, today);
      expiringSoon = !expired && isBefore(validTillDate, warningDate);
    }

    return {
      sanctioned: sanctionedAmount,
      utilized: utilizedAmount,
      available: availableAmount,
      utilizationPercent: utilization,
      latestStatus: status,
      sanctionDate: sDate,
      sanctionValidTill: sValidTill,
      isExpiringSoon: expiringSoon,
      isExpired: expired,
    };
  }, [lender, disbursements]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    try {
      return format(parseISO(dateStr), "dd MMM yyyy");
    } catch {
      return dateStr;
    }
  };

  const getUtilizationColor = () => {
    if (utilizationPercent >= 90) return "text-red-600 dark:text-red-400";
    if (utilizationPercent >= 70) return "text-yellow-600 dark:text-yellow-400";
    return "text-green-600 dark:text-green-400";
  };

  const getProgressColor = () => {
    if (utilizationPercent >= 90) return "bg-red-500";
    if (utilizationPercent >= 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getUtilizationIcon = () => {
    if (utilizationPercent >= 70) return <TrendingUp className="h-4 w-4" />;
    if (utilizationPercent >= 30) return <Minus className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center overflow-hidden">
              {lender.institutionAvatar ? (
                <img
                  src={lender.institutionAvatar.startsWith("http") ? lender.institutionAvatar : `${import.meta.env.VITE_API_BASE_URL}/${lender.institutionAvatar}`}
                  alt={lender.institutionName}
                  className="w-[80px] h-[40px] rounded-full object-cover"
                />
              ) : (
                <Building2 className="h-5 w-5 text-primary" />
              )}
            </div>
            <div>
              <h4 className="font-semibold text-sm">{lender.institutionName}</h4>
              <p className="text-xs text-muted-foreground">{lender.lenderName}</p>
            </div>
          </div>
            {latestStatus && (() => {
              const statusConfig = getStatusConfig(latestStatus as any);
              const StatusIcon = statusConfig?.icon;
              return (
              <div className="flex items-center gap-2 shrink-0">
                {StatusIcon && <StatusIcon className={`h-3.5 w-3.5 ${statusConfig?.color || ''}`} />}
                <Badge variant={statusConfig?.variant || 'secondary'} className="text-xs">
                  {latestStatus || 'Deal Shared'}
                </Badge>
              </div>
            );
            })()}
        </div>

        {/* Financial Metrics */}
        <div className="grid grid-cols-3 gap-2 mb-4">
         
          <div className="text-center p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border border-blue-200/50 dark:border-blue-800/50">
            <div className="text-xs text-muted-foreground mb-1">Sanctioned</div>
            <div className="text-sm font-bold text-blue-700 dark:text-blue-300 truncate">
              {formatCurrency(sanctioned)}
            </div>
          </div>
          <div className="text-center p-3 rounded-lg bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border border-amber-200/50 dark:border-amber-800/50">
            <div className="text-xs text-muted-foreground mb-1">Utilized</div>
            <div className="text-sm font-bold text-amber-700 dark:text-amber-300 truncate">
              {formatCurrency(utilized)}
            </div>
          </div>
          <div className="text-center p-3 rounded-lg bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20 border border-green-200/50 dark:border-green-800/50">
            <div className="text-xs text-muted-foreground mb-1">Available</div>
            <div className="text-sm font-bold text-green-700 dark:text-green-300 truncate">
              {formatCurrency(available)}
            </div>
          </div>
        </div>

        {/* Utilization Progress */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Utilization</span>
            <span className={`font-medium flex items-center gap-1 ${getUtilizationColor()}`}>
              {getUtilizationIcon()}
              {utilizationPercent.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${getProgressColor()}`}
              style={{ width: `${Math.min(100, utilizationPercent)}%` }}
            />
          </div>
        </div>

        {/* Sanction Dates */}
        <div className="p-3 rounded-lg bg-muted/30 border mb-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <div>
                <div className="text-muted-foreground">Sanction Date</div>
                <div className="font-medium">{formatDate(sanctionDate)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <div>
                <div className="text-muted-foreground">Valid Till</div>
                <div className={`font-medium flex items-center gap-1 ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600' : ''}`}>
                  {isExpired && <AlertTriangle className="h-3 w-3" />}
                  {formatDate(sanctionValidTill)}
                </div>
              </div>
            </div>
          </div>
          {isExpired && (
            <Badge variant="destructive" className="mt-2 text-xs">
              Expired
            </Badge>
          )}
          {isExpiringSoon && !isExpired && (
            <Badge variant="outline" className="mt-2 text-xs border-amber-500 text-amber-600">
              Expiring Soon
            </Badge>
          )}
        </div>

        {/* RM Info */}
        {/* {lender.applicationRM && (
          <div className="pt-3 border-t text-xs text-muted-foreground">
            RM: {lender.applicationRM}
          </div>
        )} */}
      </CardContent>
    </Card>
  );
}
